import React, { useEffect, useState } from "react";
import { app } from "./firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import CodeForm from "./CodeForm";
import Shinyu from "./Shinyu";

const auth = getAuth(app);
const db = getFirestore(app);

interface AchievementDetail {
  nameJP: string;
  description: string;
  since?: string;
  obtained?: boolean;
  obtainedAt?: string;
}

interface Achievements {
  [category: string]: {
    [achievementId: string]: boolean | { obtained?: boolean; obtainedAt?: number };
  };
}

interface GameStats {
  name: string;
  email: string;
  lastAccess: number;
  achievements: Achievements;
}

function sortLevel(a: string, b: string) {
  const level: { [key: string]: number } = {
    "legendary": 0,
    "gold": 1,
    "silver": 2,
    "topSecret": 9,
  }
  return level[a] - level[b];
}

function formatDateLocal(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).replace(/\//g, "-");
}

export default function GameStatsPage() {
  const [user, setUser] = useState(auth.currentUser);
  const [stats, setStats] = useState<GameStats | null>(null);
  const [achievementsDetails, setAchievementsDetails] = useState<Record<string, Record<string, AchievementDetail>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDescVisible, setDescVisible] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data() as GameStats;
          setStats(userData);

          // Fetch achievement details from the server
          fetch(`${process.env.PUBLIC_URL}/list.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
          })
            .then(response => response.json())
            .then(res => {
              // Merge achievements data with user's achievements
              const mergedDetails = Object.keys(res).reduce((acc, category) => {
                acc[category] = Object.keys(res[category]).reduce((subAcc, id) => {
                  const data = userData.achievements[category]?.[id];

                  subAcc[id] = {
                    ...res[category][id],
                    obtained: typeof data === 'boolean' ? data : data?.obtained ?? false,
                    obtainedAt: typeof data === 'object' && data.obtainedAt ? formatDateLocal(data.obtainedAt) : undefined
                  };

                  return subAcc;
                }, {} as Record<string, AchievementDetail>);
                return acc;
              }, {} as Record<string, Record<string, AchievementDetail>>);
              setAchievementsDetails(mergedDetails);
            });
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return <p>データを読み込んでいます...</p>;
  if (!user) return <div><h2>Bad Assort プレイ実績を集めよう</h2><p>Googleアカウントでログインすると、Bad Assortで遊んだゲームの記録を残すことができます。完全無料です。</p>
    <p>アカウントを作る前にクリアしてしまっていても、遡って実績取得が可能！</p>
    <p>あのゲームの気付かなかった要素にも気が付けるかも？　まずは右上のログインボタンからログインしてみましょう！</p>
    <img src={`${process.env.PUBLIC_URL}/img/blur.webp`} alt="実績はアカウント作成後に確認できます" style={{ width: "100%" }} /></div>;
  if (!stats || !achievementsDetails) return <div><p>初回ユーザーデータを作成しました。ページをリロードしてください。</p><p>この画面が何度も表示される場合は管理者までご連絡ください。</p></div>;

  const categoryJP: { [key: string]: string } = {
    "legendary": "プラチナ",
    "gold": "ゴールド",
    "silver": "シルバー",
    "topSecret": "シークレット"
  };

  return (
    <div>
      <h2>記録一覧</h2>
      <p>あなたの名前: {stats.name}</p>
      {Object.keys(achievementsDetails).sort(sortLevel).map((category) => (
        <div id={`achievements-${category}`} className="achievements-category">
          <h3>{categoryJP[category]}</h3>
          <ul>
            {Object.keys(achievementsDetails[category]).sort().map((id) => {
              const details = achievementsDetails[category][id];
              return (
                <li key={`achievement-${id}`} id={`achievement-${id}`}>
                  <img src={`${process.env.PUBLIC_URL}/img/medal_${details.obtained ? `${category}_${id}` : ""}.webp`} />
                  <p>{details.nameJP}</p>
                  <p style={{ display: `${isDescVisible ? "block" : "none"}` }}>{details.description}</p>
                  <p style={{ color: `${details.obtained ? "red" : "inherit"}` }}>{details.obtained ? `${details.obtainedAt}` : "未取得"}</p>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <CodeForm></CodeForm>
      <Shinyu></Shinyu>
    </div>
  );
}
